// SCR-STAFF-108 — تنظیمات مراجعه‌کننده /settings/patients (US-PAT-003، US-PAT-063): الگوی شماره پرونده (پیشوند، حداقل طول، شماره
// شروع با پیش‌نمایش؛ شروع پایین‌تر از شماره بعدی PHX-PAT-0701) و منابع معرفی (افزودن، ویرایش، غیرفعال‌سازی).
// AP-PAT-02: استعلام خودکار و روزهای پرونده موقت، برچسب‌های مدیریت‌شده و قالب‌های رضایت (PatientSettingsPhase1).
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type FileNumberSettingsRequest,
  type ReferralSourceKind,
  type ReferralSourceRequest,
  type ReferralSourceView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  keepDigitsOnly,
  LoadingState,
  PageHeader,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  useSaveShortcut,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ConsentTemplatesSection, PreferencesSection, TagDefinitionsSection } from './PatientSettingsPhase1';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { patKeys, useReferralSources } from './common';

export const SCR_STAFF_108 = 'SCR-STAFF-108';

const KINDS: readonly ReferralSourceKind[] = ['Social', 'Referrer', 'Physician', 'Campaign', 'Website', 'WalkIn', 'Other'];

const fileNumberSchema = z.object({
  prefix: z.string().regex(/^[A-Za-z0-9-]{0,10}$/, { error: 'errors:validation.file_number_pattern' }),
  minLength: z.string().refine((value) => /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 12, {
    error: 'errors:validation.file_number_pattern',
  }),
  startNumber: z.string().refine((value) => /^\d+$/.test(value) && Number(value) >= 1, { error: 'common:validation.required' }),
});
type FileNumberForm = z.input<typeof fileNumberSchema>;

function FileNumberSection({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  const settings = useQuery({
    queryKey: patKeys.fileNumber(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/settings/file-number')),
  });
  const values = useMemo<FileNumberForm>(
    () => ({
      prefix: settings.data?.prefix ?? '',
      minLength: String(settings.data?.minLength ?? 5),
      startNumber: String(settings.data?.startNumber ?? 1),
    }),
    [settings.data],
  );
  const { control, handleSubmit, setError, formState } = useForm<FileNumberForm>({
    resolver: zodResolver(fileNumberSchema),
    values,
  });

  const save = useMutation({
    mutationFn: (body: FileNumberSettingsRequest) => unwrap(tenantApi.PUT('/api/v1/patients/settings/file-number', { body })),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(patKeys.fileNumber(session?.tenantId), data);
      setSaved(true);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['prefix', 'minLength', 'startNumber'],
        codeFields: { 'PHX-PAT-0701': 'startNumber' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  const submit = handleSubmit((form) =>
    save.mutate({
      prefix: form.prefix || null,
      minLength: Number(keepDigitsOnly(form.minLength)),
      startNumber: Number(keepDigitsOnly(form.startNumber)),
    }),
  );
  useSaveShortcut(() => void submit(), canWrite && !save.isPending);

  if (settings.isPending) {
    return <LoadingState rows={2} />;
  }
  if (settings.error) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box component="form" noValidate onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <Typography variant="h2" gutterBottom>
          {t('pat:settings.file_number.title')}
        </Typography>
        <Stack spacing={2}>
          {saved && <Alert severity="success">{t('pat:settings.file_number.saved')}</Alert>}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextFieldRHF
              control={control}
              name="prefix"
              labelKey="pat:settings.file_number.prefix"
              helperKey="pat:settings.file_number.prefix_help"
              fullWidth
              disabled={!canWrite}
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'file-number-prefix' } }}
            />
            <TextFieldRHF
              control={control}
              name="minLength"
              labelKey="pat:settings.file_number.min_length"
              fullWidth
              disabled={!canWrite}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'file-number-min-length' } }}
            />
            <TextFieldRHF
              control={control}
              name="startNumber"
              labelKey="pat:settings.file_number.start_number"
              helperKey="pat:settings.file_number.start_help"
              fullWidth
              disabled={!canWrite}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'file-number-start' } }}
            />
          </Stack>
          <Typography data-testid="file-number-preview">
            {t('pat:settings.file_number.preview', { preview: faDigits(settings.data.preview) })}
          </Typography>
          {submitError ? <ErrorState error={submitError} compact /> : null}
          {canWrite && (
            <Box>
              <Button type="submit" variant="contained" disabled={save.isPending || !formState.isDirty} data-testid="file-number-save">
                {t('common:actions.save')}
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

const sourceSchema = z.object({
  code: z.string().regex(/^[a-z][a-z0-9_-]{0,39}$/, { error: 'errors:validation.referral_source_code' }),
  name: zRequiredText(100),
  kind: z.enum(['Social', 'Referrer', 'Physician', 'Campaign', 'Website', 'WalkIn', 'Other']),
  isActive: z.boolean(),
  sortOrder: z.string().regex(/^\d{0,4}$/, { error: 'common:validation.too_long' }),
});
type SourceForm = z.input<typeof sourceSchema>;

function SourceDialog({ source, open, onClose }: { source: ReferralSourceView | null; open: boolean; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = source === null;
  const values = useMemo<SourceForm>(
    () => ({
      code: source?.code ?? '',
      name: source?.name ?? '',
      kind: source?.kind ?? 'Other',
      isActive: source?.isActive ?? true,
      sortOrder: String(source?.sortOrder ?? 100),
    }),
    [source],
  );
  const { control, handleSubmit, formState, setError, reset } = useForm<SourceForm>({ resolver: zodResolver(sourceSchema), values });
  useEffect(() => {
    if (open) {
      setSubmitError(undefined);
    }
  }, [open]);

  const save = useMutation({
    mutationFn: (body: ReferralSourceRequest) =>
      creating
        ? unwrap(tenantApi.POST('/api/v1/patients/referral-sources', { body, headers: { 'Idempotency-Key': idempotency.current() } }))
        : unwrap(tenantApi.PUT('/api/v1/patients/referral-sources/{id}', { params: { path: { id: source.id } }, body })),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...patKeys.all(session?.tenantId), 'referral-sources'] });
      reset();
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'kind', 'sortOrder'],
        codeFields: { 'PHX-PAT-0603': 'code' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open={open}
      titleKey={creating ? 'pat:settings.sources.add' : 'pat:settings.sources.edit'}
      screenId="SCR-STAFF-108-SOURCE"
      onSubmit={() =>
        void handleSubmit((form) =>
          save.mutate({
            code: form.code,
            name: form.name,
            kind: form.kind,
            isActive: form.isActive,
            sortOrder: form.sortOrder ? Number(form.sortOrder) : null,
          }),
        )()
      }
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <TextFieldRHF
        control={control}
        name="code"
        labelKey="pat:settings.sources.code"
        helperKey="pat:settings.sources.code_help"
        required
        fullWidth
        disabled={!creating}
        slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'source-code' } }}
      />
      <TextFieldRHF control={control} name="name" labelKey="pat:settings.sources.name" required fullWidth slotProps={{ htmlInput: { 'data-testid': 'source-name' } }} />
      <SelectRHF
        control={control}
        name="kind"
        labelKey="pat:settings.sources.kind_label"
        fullWidth
        options={KINDS.map((kind) => ({ value: kind, labelKey: `pat:settings.sources.kind.${kind}` }))}
      />
      <TextFieldRHF control={control} name="sortOrder" labelKey="pat:settings.sources.sort_order" fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }} />
      {!creating && <CheckboxRHF control={control} name="isActive" labelKey="pat:settings.sources.active" />}
    </FormDialog>
  );
}

function SourcesSection({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const sources = useReferralSources(true);
  const [editing, setEditing] = useState<ReferralSourceView | null | undefined>(undefined);
  const columns: DataTableColumn<ReferralSourceView>[] = [
    { id: 'name', headerKey: 'pat:settings.sources.name', cell: (row) => row.name },
    { id: 'code', headerKey: 'pat:settings.sources.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'kind', headerKey: 'pat:settings.sources.kind_label', cell: (row) => t(`pat:settings.sources.kind.${row.kind}`) },
    { id: 'order', headerKey: 'pat:settings.sources.sort_order', cell: (row) => faDigits(row.sortOrder) },
    { id: 'status', headerKey: 'pat:settings.sources.active', cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} /> },
    {
      id: 'actions',
      headerKey: 'pat:settings.sources.edit',
      cell: (row) =>
        canWrite ? (
          <IconButton aria-label={t('pat:settings.sources.edit')} onClick={() => setEditing(row)} data-testid={`source-edit-${row.code}`}>
            <EditOutlinedIcon />
          </IconButton>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {t('pat:settings.sources.title')}
        </Typography>
        {canWrite && (
          <Button startIcon={<AddIcon />} onClick={() => setEditing(null)} data-testid="source-add">
            {t('pat:settings.sources.add')}
          </Button>
        )}
      </Stack>
      <DataTable
        captionKey="pat:settings.sources.title"
        columns={columns}
        rows={sources.data}
        getRowId={(row) => row.id}
        isLoading={sources.isPending}
        error={sources.error}
        onRetry={() => void sources.refetch()}
      />
      <SourceDialog source={editing ?? null} open={editing !== undefined} onClose={() => setEditing(undefined)} />
    </Paper>
  );
}

export function PatientSettingsPage() {
  const write = useWriteAccess('pat.settings.manage');
  return (
    <Box data-screen-id={SCR_STAFF_108} component="section">
      <PageHeader titleKey="pat:settings.title" subtitleKey="pat:settings.subtitle" />
      <Stack spacing={2}>
        <WriteNotice write={write} />
        <FileNumberSection canWrite={write.canWrite} />
        <SourcesSection canWrite={write.canWrite} />
        <PreferencesSection canWrite={write.canWrite} />
        <TagDefinitionsSection canWrite={write.canWrite} />
        <ConsentTemplatesSection canWrite={write.canWrite} />
      </Stack>
    </Box>
  );
}
